import { ConfigurationType, LevelType, SystemOptionType } from 'data/types';
import {
  heatingWeightForSpace,
  requirementLoadWeight
} from 'utils/space-heating-weight';
import { findSpaceById } from 'utils/structure';

export type RequiredShareRatios = {
  heatingRequiredRatio: number;
  coolingRequiredRatio: number;
  // Whether any space asks this system for the side at all. A null coverage
  // score reads as "not required" only when this is false; otherwise it is a
  // data gap.
  heatingRequired: boolean;
  coolingRequired: boolean;
};

function shareRatio(required: number, load: number): number {
  if (load <= 0) return 1;
  return required / load;
}

export function requiredShareRatiosForConfiguration(
  config: ConfigurationType,
  structure: LevelType[],
  supplementalHeatingWeight: number,
  supplementalCoolingWeight: number
): RequiredShareRatios {
  let heatingLoad = 0;
  let heatingRequired = 0;
  let coolingLoad = 0;
  let coolingRequired = 0;

  config.condensers.forEach((condenser) => {
    condenser.zones.forEach((zone) => {
      zone.zoneItems.forEach((item) => {
        const space = findSpaceById(structure, item.structureItemId);
        const heatingValue = space?.attributes.find(
          (attr) => attr.code === 'heating'
        )?.value;
        const coolingValue = space?.attributes.find(
          (attr) => attr.code === 'cooling'
        )?.value;
        const heatingBTU = item.BTUs?.heating ?? 0;
        const coolingBTU = item.BTUs?.cooling ?? 0;
        heatingLoad += Math.round(
          requirementLoadWeight(heatingValue) * heatingBTU
        );
        coolingLoad += Math.round(
          requirementLoadWeight(coolingValue) * coolingBTU
        );
        heatingRequired += Math.round(
          heatingWeightForSpace(heatingValue, supplementalHeatingWeight) *
            heatingBTU
        );
        coolingRequired += Math.round(
          heatingWeightForSpace(coolingValue, supplementalCoolingWeight) *
            coolingBTU
        );
      });
    });
  });

  return {
    heatingRequiredRatio: shareRatio(heatingRequired, heatingLoad),
    coolingRequiredRatio: shareRatio(coolingRequired, coolingLoad),
    heatingRequired: heatingLoad > 0,
    coolingRequired: coolingLoad > 0
  };
}

export function coolingRequiredOnOption(system: SystemOptionType): boolean {
  const value = (system as OptionWithRatios).coolingRequired;
  // Options built before the flag existed never carried a null score for
  // "not required", so a missing flag means cooling was asked for.
  return typeof value === 'boolean' ? value : true;
}

type OptionWithRatios = SystemOptionType & Partial<RequiredShareRatios>;

export function requiredRatioOnOption(
  system: SystemOptionType,
  side: 'heating' | 'cooling'
): number {
  const value = (system as OptionWithRatios)[
    side === 'heating' ? 'heatingRequiredRatio' : 'coolingRequiredRatio'
  ];
  return typeof value === 'number' && Number.isFinite(value) ? value : 1;
}
